import { setExpandedAccordionOnDetailsPage, setSelectedTabOnDetailsPage } from 'component/page/detail/store/details';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import { ElementEventIds } from '@agoda/analytics-messages-definition-activities';
import {
    ActivitiesAdditionalInfoContainerClickEventActivitiesFieldsV3,
    ActivitiesCancellationContainerClickEventActivitiesFieldsV3,
    ActivitiesIntroductionSummaryContainerClickEventActivitiesFieldsV1,
    ActivitiesMosaicGalleryClickEventActivitiesFieldsV1,
    ActivitiesReviewContainerClickEventActivitiesFieldsV1,
    ActivitiesSimCardContainerClickEventActivitiesFieldsV1,
} from '@agoda/analytics-messages-definition-activities/events.split';
import { createSelector } from '@reduxjs/toolkit';
import { selectIsScrolling } from 'component/page/detail/store/detail.selectors';
import { selectConfigFromBootstrap } from 'store/slice/bootstrap';
import React from 'react';
import { ActivitiesMosaicGalleryClickEventActivitiesManifestV1 } from '@agoda/analytics-messages-definition-activities/manifest/events';
import { selectOfferSelected } from 'component/page/detail/store/availabilitySelectorIndex';
import { selectTabs, Tab } from 'component/page/detail/DetailPageTabs/DetailPageTabs.selectors';
import { ScreenSize, selectScreenSize } from 'store/slice/screen';
import { isOn } from 'core/window/bootstrapConfig';
import { FeatureName } from 'core/constant/FeatureName';

export enum FooterType {
    None,
    Booking,
    Details,
}

interface DispatchProps {
    sendAccordionClickAnalytics: (isExpanded: boolean, selector: string, eventIds: ElementEventIds) => void;
    onImageClick: (event: React.MouseEvent, index: number) => void;
    setExpandedAccordionOnDetailsPage: (href: string) => void;
}

interface StateProps {
    screenSize: ScreenSize;
    isScrolling: boolean;
    isSelectedOfferCardExist: boolean;
    footerType: FooterType;
    tabs: Tab[];
    recentReviews: boolean;
}

export type DetailPageContentProps = DispatchProps & StateProps;

type AccordionEventFields =
    | ActivitiesAdditionalInfoContainerClickEventActivitiesFieldsV3
    | ActivitiesCancellationContainerClickEventActivitiesFieldsV3
    | ActivitiesIntroductionSummaryContainerClickEventActivitiesFieldsV1
    | ActivitiesSimCardContainerClickEventActivitiesFieldsV1
    | ActivitiesReviewContainerClickEventActivitiesFieldsV1;

export interface DetailContentOnToggleProps {
    onToggle: (selector: string | undefined, eventIds: ElementEventIds) => (isExpanded: boolean) => void;
    isScrolling: boolean;
    href?: string;
}

export const mapDispatchToProps = (dispatch: Function): DispatchProps => ({
    sendAccordionClickAnalytics: (isExpanded: boolean, selector: string, eventIds: ElementEventIds) => {
        trackElementEvent<AccordionEventFields>(eventIds, { is_toggle_on: isExpanded });
        dispatch(setExpandedAccordionOnDetailsPage({ selector, status: isExpanded }));
        dispatch(setSelectedTabOnDetailsPage(selector));
    },
    onImageClick: (event: React.MouseEvent, index: number) => {
        trackElementEvent<ActivitiesMosaicGalleryClickEventActivitiesFieldsV1>(ActivitiesMosaicGalleryClickEventActivitiesManifestV1.ids, {}, { action_element_index: index + 1 });
    },
    setExpandedAccordionOnDetailsPage: (href: string) => {
        dispatch(setExpandedAccordionOnDetailsPage({ selector: `#${href}`, status: true }));
    },
});

export const mapStateToProps = createSelector(
    selectScreenSize,
    selectIsScrolling,
    selectConfigFromBootstrap,
    selectOfferSelected,
    selectTabs,
    (screenSize, isScrolling, config, selectedOffer, tabsObject): StateProps => {
        const isSelectedOfferCardExist = selectedOffer?.offerIndex !== undefined && selectedOffer?.offerIndex > -1;
        const footerType = isSelectedOfferCardExist ? (screenSize.desktop ? FooterType.None : FooterType.Booking) : FooterType.Details;
        const recentReviews = isOn(FeatureName.ActivitiesRecentReviews, config);
        return {
            screenSize,
            isScrolling,
            isSelectedOfferCardExist,
            footerType,
            ...tabsObject,
            recentReviews,
        };
    },
);
